import axios from 'axios';
import { Klydo } from './klydo';
import { baseUrl } from './baseRequest';
import { User } from './user';
import { ApprovedCollection } from './collections';

export type KlydoPreview = Pick<
  Klydo,
  | 'chimesUrls'
  | 'capabilitiesV2'
  | 'name'
  | 'subtitle'
  | 'createdAt'
  | 'stats'
  | 'id'
  | 'metadata'
  | 'tags'
  | 'pendulumLogo'
> & {
  author: User;
};

export type PublicCollectionPreview = ApprovedCollection & {
  creator: User;
  klydos: KlydoPreview[];
};

export type CollectionWithCode = {
  collection: ApprovedCollection;
  code: string;
};

export type CollectionCodeResponse =
  | { status: 'badRequest' }
  | { status: 'inProgress' }
  | { status: 'completed'; collections: CollectionWithCode[] };

export enum SectionFieldType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  IMAGE = 'image',
  VIDEO = 'video',
  SELECT = 'select',
}

export enum SectionTemplateKey {
  FULL_WIDTH_VIDEO = 'fullWidthVideo',
  VIDEO_TEXT = 'videoText',
  IMAGE_TEXT = 'imageText',
  FULL_WIDTH_IMAGE = 'fullWidthImage',
  DOUBLE_IMAGE = 'doubleImage',
}

export type ArtistProfileTemplateField = {
  key: string;
  label: string;
  type: SectionFieldType;
  options?: string[];
  group?: string;
};

export type ArtistProfileTemplate = {
  key: string;
  name: string;
  description: string;
  fields: ArtistProfileTemplateField[];
};

export type PublicApiClient = {
  getKlydo: (id: string) => Promise<KlydoPreview>;
  getCollection: (id: string) => Promise<PublicCollectionPreview>;
  getCollectionCodes: (
    confirmationNumber: string,
    orderId: string,
  ) => Promise<CollectionCodeResponse>;
};

export const publicClient = {
  get: async <T>({
    url,
    params,
  }: {
    url: string;
    params?: object;
  }): Promise<T> => {
    return axios.get(baseUrl + url, { params }).then((res) => res.data);
  },
};

const publicApiClient: PublicApiClient = {
  getKlydo: (id: string) => {
    return publicClient.get<KlydoPreview>({
      url: `/public/klydo-preview/${id}`,
    });
  },
  getCollection: (id: string) => {
    return publicClient.get<PublicCollectionPreview>({
      url: `/public/collection-preview/${id}`,
    });
  },
  getCollectionCodes: (confirmationNumber: string, orderId: string) => {
    return publicClient.get<CollectionCodeResponse>({
      url: `/public/collection-codes`,
      params: { confirmationNumber, orderId },
    });
  },
};

export default publicApiClient;
